import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Search, FileText, AlertTriangle, GitBranch, ClipboardCheck, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const TYPE_ICONS = {
  contract: FileText,
  claim: AlertTriangle,
  variation: GitBranch,
  obligation: ClipboardCheck,
  strategy: FileText,
  bid_package: FileText,
};

const TYPE_ROUTES = {
  contract: (id) => `/app/contracts/${id}`,
  claim: (id) => `/app/claims/${id}`,
  variation: () => '/app/variations',
  obligation: () => '/app/cm/obligation-tracking',
  strategy: (id) => `/app/scm/strategy/${id}`,
  bid_package: (id) => `/app/scm/strategy/${id}`,
};

const TYPE_COLORS = {
  contract: 'text-[#1E4E8C]',
  claim: 'text-amber-600',
  variation: 'text-blue-600',
  obligation: 'text-emerald-600',
  strategy: 'text-purple-600',
  bid_package: 'text-indigo-600',
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/notifications/search?q=${encodeURIComponent(q)}`);
      setResults(res.data.results || []);
      setSelected(0);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (result) => {
    setOpen(false);
    setQuery('');
    const route = TYPE_ROUTES[result.type];
    if (route) navigate(route(result.id));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && results[selected]) { handleSelect(results[selected]); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-trigger"
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 text-gray-500 text-xs cursor-pointer hover:bg-gray-200 transition-colors">
        <Search className="w-3.5 h-3.5" />
        <span>Search contracts, claims...</span>
        <kbd className="ml-6 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-[10px] font-data text-gray-400">Ctrl+K</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl p-0 gap-0 bg-white border-gray-200 overflow-hidden" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Global Search</DialogTitle>
          <DialogDescription className="sr-only">Search across contracts, claims, and variations</DialogDescription>
          <div className="flex items-center gap-3 px-4 border-b border-gray-200">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search contracts, claims, variations..."
              className="border-0 bg-transparent h-12 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
              data-testid="global-search-input"
              autoFocus
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {results.length === 0 && query.length >= 2 && !loading && (
              <p className="text-sm text-gray-400 text-center py-8">No results found</p>
            )}
            {results.length === 0 && query.length < 2 && (
              <p className="text-sm text-gray-400 text-center py-8">Type at least 2 characters to search</p>
            )}
            {results.map((r, i) => {
              const Icon = TYPE_ICONS[r.type] || FileText;
              return (
                <button key={`${r.type}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${i === selected ? 'bg-[#1E4E8C]/10' : 'hover:bg-gray-50'}`}
                  data-testid={`search-result-${r.ref}`}>
                  <Icon className={`w-4 h-4 shrink-0 ${TYPE_COLORS[r.type]}`} strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-data text-xs text-[#1E4E8C]">{r.ref}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase">{r.type}</span>
                    </div>
                    <p className="text-sm text-gray-700 truncate mt-0.5">{r.title}</p>
                  </div>
                  {r.status && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">{r.status}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-200 text-[10px] text-gray-400">
            <span><kbd className="px-1 py-0.5 rounded bg-gray-100 font-data">Enter</kbd> to select</span>
            <span><kbd className="px-1 py-0.5 rounded bg-gray-100 font-data">Esc</kbd> to close</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
